import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const AnimatedPopup = ({ 
  isOpen, 
  onClose, 
  type = 'success', // success, error, info, warning
  title, 
  message, 
  duration = 5000, // auto close after 5 seconds
  position = 'top-right' // top-right, top-left, bottom-right, bottom-left, center
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Trigger animation after visibility
      setTimeout(() => setIsAnimating(true), 10);
      
      // Auto close if duration is set
      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'error':
        return <AlertCircle className="text-red-500" size={24} />;
      case 'warning':
        return <AlertTriangle className="text-orange-500" size={24} />;
      case 'info':
      default:
        return <Info className="text-blue-500" size={24} />;
    }
  };

  const getColorClasses = () => {
    switch (type) {
      case 'success':
        return 'border-green-200 bg-green-50/95';
      case 'error':
        return 'border-red-200 bg-red-50/95';
      case 'warning':
        return 'border-orange-200 bg-orange-50/95';
      case 'info':
      default:
        return 'border-blue-200 bg-blue-50/95';
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-6 left-6';
      case 'bottom-right':
        return 'bottom-6 right-6';
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'center':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'top-right':
      default:
        return 'top-6 right-6';
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed z-50 ${getPositionClasses()}`}>
      <div
        className={`
          glass-card border-2 rounded-xl p-4 max-w-sm shadow-2xl backdrop-blur-md
          transition-all duration-300 ease-out transform
          ${getColorClasses()}
          ${isAnimating 
            ? 'translate-y-0 opacity-100 scale-100' 
            : position === 'center' 
              ? 'scale-95 opacity-0'
              : position.includes('top')
                ? '-translate-y-4 opacity-0 scale-95'
                : 'translate-y-4 opacity-0 scale-95'
          }
        `}
        style={{
          animation: isAnimating ? 'morphIn 0.3s ease-out forwards' : undefined
        }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            {title && (
              <h4 className="font-semibold text-text-primary text-sm mb-1">
                {title}
              </h4>
            )}
            <p className="text-text-secondary text-sm leading-relaxed">
              {message}
            </p>
          </div>
          
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-text-muted hover:text-text-primary transition-colors duration-200 p-1 hover:bg-white/50 rounded-full"
          >
            <X size={16} />
          </button>
        </div>
        
        {/* Progress bar for auto-close */}
        {duration > 0 && (
          <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ease-linear ${
                type === 'success' ? 'bg-green-500' :
                type === 'error' ? 'bg-red-500' :
                type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
              }`}
              style={{
                width: isAnimating ? '0%' : '100%',
                transition: `width ${duration}ms linear`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimatedPopup;